// Project delete rules (SEQ-212 finding d).
//
// Production RLS lets only an organization admin or super_admin delete a row
// in claims_projects. Every child table references claims_projects with
// ON DELETE CASCADE (categories and subcategories, progress, variations, hire,
// notes, reports and the rest), so ONE delete of the project row removes the
// whole project in a single statement. That is also the safest shape:
//
// - The old code deleted children first (progress, subcategories, variations,
//   mail references, members, categories) and the project row last. A member
//   may delete several of those children but not the project, so a member's
//   delete emptied the project and then failed silently on the last step.
// - Now the project row goes first. If the database refuses it (not an admin,
//   or a foreign key with no cascade), the statement removes nothing.

// Minimal shape of the supabase-js client that the delete uses.
export interface ProjectDeleteClient {
  from(table: string): {
    delete(): {
      eq(
        column: string,
        value: string,
      ): {
        select(
          columns: string,
        ): PromiseLike<{
          data: Array<{ id: string }> | null;
          error: { message: string } | null;
        }>;
      };
    };
  };
}

// Same rule as the claims_projects DELETE policy: org admin or super_admin.
export function canDeleteProject(
  membership: { role?: string | null } | null | undefined,
): boolean {
  return membership?.role === "admin" || membership?.role === "super_admin";
}

export class ProjectDeleteRefusedError extends Error {
  constructor() {
    super("You do not have permission to delete this project.");
    this.name = "ProjectDeleteRefusedError";
  }
}

// Delete the project row and nothing else. Throws when the database reports
// an error, and throws ProjectDeleteRefusedError when it deleted no row (RLS
// answers a refused delete with zero rows and no error).
export async function deleteProjectRow(
  client: ProjectDeleteClient,
  projectId: string,
  isAdmin: boolean,
): Promise<void> {
  if (!isAdmin) throw new ProjectDeleteRefusedError();
  const { data, error } = await client
    .from("claims_projects")
    .delete()
    .eq("id", projectId)
    .select("id");
  if (error) throw error;
  if (!data || data.length === 0) throw new ProjectDeleteRefusedError();
}
